import { useState } from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import type { HistoryChange } from './changeLogTypes';
import { formatPrimitive, isPrimitive } from './formatChange';

/** Objects and lists are long: collapsed behind "Show details", as indented JSON. */
const ChangeValue: React.FC<{ value: unknown }> = ({ value }) => {
  const [open, setOpen] = useState(false);
  if (isPrimitive(value)) return <Typography.Text code>{formatPrimitive(value)}</Typography.Text>;
  return (
    <span>
      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide details' : 'Show details'}
      </Button>
      {open && (
        <pre style={{ margin: '4px 0', fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </span>
  );
};

const NameChips: React.FC<{ title: string; names: string[]; color: string }> = ({
  title,
  names,
  color,
}) => (
  <Space size={4} wrap>
    <Typography.Text type="secondary">{title}:</Typography.Text>
    {names.map((name) => (
      <Tag key={name} color={color}>
        {name}
      </Tag>
    ))}
  </Space>
);

/** One field of a change: what was added and removed, or what it was and what it became. */
export const ChangeLine: React.FC<{ change: HistoryChange }> = ({ change }) => {
  const added = change.added ?? [];
  const removed = change.removed ?? [];
  let body: React.ReactNode;
  if (change.redacted) {
    body = <Typography.Text type="secondary">changed (value hidden)</Typography.Text>;
  } else if (added.length > 0 || removed.length > 0) {
    body = (
      <Space direction="vertical" size={2}>
        {added.length > 0 && <NameChips title="added" names={added} color="green" />}
        {removed.length > 0 && <NameChips title="removed" names={removed} color="red" />}
      </Space>
    );
  } else {
    body = (
      <Space size={4} wrap>
        <ChangeValue value={change.before} />
        <span aria-label="changed to">→</span>
        <ChangeValue value={change.after} />
      </Space>
    );
  }
  return (
    <div>
      <Typography.Text strong>{change.label}</Typography.Text>
      <div>{body}</div>
    </div>
  );
};
